import React, { useState } from 'react';
import { ArrowLeft, Bell, Globe, Moon, Sun, Mail, Lock, Shield } from 'lucide-react';

/**
 * Settings Page - User preferences and settings
 * Manage notifications, appearance, language, and account settings
 */
function SettingsPage({ onBack, userProfile }) {
  const [settings, setSettings] = useState({
    // Notifications
    emailNotifications: true,
    reservationUpdates: true,
    newMessages: true,
    marketingEmails: false,
    
    // Appearance
    darkMode: false,
    compactView: false,
    
    // Language
    language: 'en',
    
    // Privacy
    profileVisible: true,
    showActivity: true,
  });

  const handleToggle = (key) => {
    setSettings({ ...settings, [key]: !settings[key] });
    console.log('Setting updated:', key, !settings[key]);
    // TODO: Save to Supabase
  };

  const handleLanguageChange = (lang) => {
    setSettings({ ...settings, language: lang });
    console.log('Language changed to:', lang);
    // TODO: Save to Supabase
  };

  const SettingSection = ({ icon: Icon, title, children }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );

  const ToggleSetting = ({ label, description, enabled, onToggle }) => (
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
      </div>
      <button
        onClick={onToggle}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          enabled ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            enabled ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );

  const userDisplayName = userProfile?.name || 'Propshare Guest';
  const userEmail = userProfile?.email || '';
  const userOrganization = userProfile?.organization || 'Propshare Network';
  const userRole = userProfile?.role || 'Guest';

  return (
    <div className="fixed inset-0 bg-gray-50 z-[200]">
      <div className="h-full overflow-y-auto">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 py-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back to Search</span>
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
              <p className="text-sm text-gray-600">Manage your preferences and account settings</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="bg-white rounded-lg border border-blue-100 p-5 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Signed in as</p>
          <p className="text-xl font-semibold text-gray-900">{userDisplayName}</p>
          <p className="text-sm text-gray-600">{userEmail}</p>
          <p className="text-sm text-gray-500">{userOrganization}</p>
          <p className="text-xs text-gray-400 mt-2">Role: {userRole}</p>
        </div>
        {/* Notifications */}
        <SettingSection icon={Bell} title="Notifications">
          <ToggleSetting
            label="Email Notifications"
            description="Receive email updates about your account"
            enabled={settings.emailNotifications}
            onToggle={() => handleToggle('emailNotifications')}
          />
          <ToggleSetting
            label="Reservation Updates"
            description="Get notified when your reservation status changes"
            enabled={settings.reservationUpdates}
            onToggle={() => handleToggle('reservationUpdates')}
          />
          <ToggleSetting
            label="New Messages"
            description="Receive alerts for new messages from warehouses"
            enabled={settings.newMessages}
            onToggle={() => handleToggle('newMessages')}
          />
          <ToggleSetting
            label="Marketing Emails"
            description="Receive updates about new features and tips"
            enabled={settings.marketingEmails}
            onToggle={() => handleToggle('marketingEmails')}
          />
        </SettingSection>

        {/* Appearance */}
        <SettingSection icon={settings.darkMode ? Moon : Sun} title="Appearance">
          <ToggleSetting
            label="Dark Mode"
            description="Use dark theme throughout the app"
            enabled={settings.darkMode}
            onToggle={() => handleToggle('darkMode')}
          />
          <ToggleSetting
            label="Compact View"
            description="Show more items on screen at once"
            enabled={settings.compactView}
            onToggle={() => handleToggle('compactView')}
          />
        </SettingSection>

        {/* Language */}
        <SettingSection icon={Globe} title="Language">
          <div>
            <p className="text-sm font-medium text-gray-900 mb-3">Select Language</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleLanguageChange('en')}
                className={`px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                  settings.language === 'en'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => handleLanguageChange('es')}
                className={`px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                  settings.language === 'es'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Español
              </button>
            </div>
          </div>
        </SettingSection>

        {/* Privacy */}
        <SettingSection icon={Shield} title="Privacy">
          <ToggleSetting
            label="Profile Visible"
            description="Allow other users to see your profile"
            enabled={settings.profileVisible}
            onToggle={() => handleToggle('profileVisible')}
          />
          <ToggleSetting
            label="Show Activity"
            description="Display your recent activity to others"
            enabled={settings.showActivity}
            onToggle={() => handleToggle('showActivity')}
          />
        </SettingSection>

        {/* Account Actions */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Lock className="w-5 h-5 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Account</h3>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Signed in with {userEmail || 'your account'} ({userRole})
          </p>
          <div className="space-y-3">
            <button className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left">
              Change Password
            </button>
            <button className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left">
              Download My Data
            </button>
            <button className="w-full px-4 py-2 border border-red-300 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors text-left">
              Delete Account
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default SettingsPage;
